import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, ListRenderItemInfo, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { FirstTimePurchaseBanner } from '@/feature/deposit/components/FirstTimePurchaseBanner';
import { setCoinPackage } from '@/feature/deposit/hooks/use-deposit-status-store';
import { hasUserPurchasedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { CoinPackageScreenNavigationProp } from '@/navigation/deposit/types';
import { navigateToKyc } from '@/navigation/navigation';
import { common } from '@/styles/styles';

import { CoinPackagesType, useCoinPackages } from '../hooks/use-coin-packages';
import { getPackageRewards, isMostPopularPackage } from '../utils/coin-package-rewards';
import { CoinPackageCard } from './CoinPackageCard';
import { DailyBonusSection } from './DailyBonusSection';

const ITEM_SPACING = 12;

type CoinPackageListProps = {
    isTrustlyKyc?: boolean;
};

export const CoinPackageList = ({ isTrustlyKyc = false }: CoinPackageListProps) => {
    const navigation = useNavigation<CoinPackageScreenNavigationProp>();
    const safeInsets = useSafeAreaInsets();
    const { t } = useTranslation(['common', 'wallet', 'sweeps']);
    const { data: products, isLoading } = useCoinPackages();
    const { data: userHasPurchased } = useAuthUserConfig({
        select: hasUserPurchasedSelector,
    });

    const showPurchaseSummary = useCallback(
        (item: CoinPackagesType) => {
            setCoinPackage(item);
            if (isTrustlyKyc) {
                navigateToKyc(navigation, { screen: 'TrustlyWebview' });
            } else {
                navigation.navigate('DepositScreen', {});
            }
        },
        [navigation, isTrustlyKyc]
    );

    const renderFirstTimePurchaseHeader = () => {
        if (userHasPurchased) {
            return null;
        }

        return (
            <>
                <SizedBox value={2} />
                <FirstTimePurchaseBanner />
                <SizedBox value={20} />
            </>
        );
    };

    const renderProductItem = ({ item }: ListRenderItemInfo<CoinPackagesType>) => (
        <CoinPackageCard
            item={item}
            rewards={getPackageRewards(item.coin_package_id, t)}
            isMostPopular={isMostPopularPackage(item.coin_package_id)}
            onPress={showPurchaseSummary}
            showFirstTimeBoost={!userHasPurchased}
        />
    );

    if (isLoading) {
        return (
            <View style={[common.alignCenter, common.justifyCenter, common.fullHeight]}>
                <LoadingSpinner />
            </View>
        );
    }

    return (
        <FlatList
            ListHeaderComponent={renderFirstTimePurchaseHeader()}
            data={products?.coin_packages ?? []}
            keyExtractor={item => item.coin_package_id.toString()}
            renderItem={renderProductItem}
            ItemSeparatorComponent={ItemSeparator}
            contentContainerStyle={[common.paddingHorizontal, { paddingBottom: safeInsets.bottom }]}
            ListEmptyComponent={<EmptyComponent />}
            ListFooterComponent={<FooterComponent />}
        />
    );
};

const ItemSeparator = () => <SizedBox value={ITEM_SPACING} />;

const FooterComponent = () => {
    const { t } = useTranslation('wallet');
    return (
        <>
            <DailyBonusSection />
            <Box mt={'s12'}>
                <Text variant="bodySmall" color="neutral300" textAlign="center">
                    {t('no_purchase_necessary_copy')}
                </Text>
            </Box>
        </>
    );
};

const EmptyComponent = () => {
    const { t } = useTranslation('common');
    return (
        <View style={[common.full, common.alignCenter]}>
            <SizedBox value={180} />
            <Text variant="titleLarge">{t('no_products_available')}</Text>
        </View>
    );
};
